import moment from 'moment';
import {
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../constants/NoteTypes';
import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { getFileLink } from './FileAccessUtils';
import { getLinkFallbackTitle, getLinkHostname, splitTextByLinks } from './LinkUtils';

export const NOTE_MEDIA_TYPE_PHOTO = 'photo';
export const NOTE_MEDIA_TYPE_VIDEO = 'video';
export const NOTE_MEDIA_TYPE_AUDIO = 'audio';
export const NOTE_MEDIA_TYPE_FILE = 'file';
export const NOTE_MEDIA_TYPE_LINK = 'link';

export type NoteMediaType =
	| typeof NOTE_MEDIA_TYPE_PHOTO
	| typeof NOTE_MEDIA_TYPE_VIDEO
	| typeof NOTE_MEDIA_TYPE_AUDIO
	| typeof NOTE_MEDIA_TYPE_FILE
	| typeof NOTE_MEDIA_TYPE_LINK;

export interface NoteMediaItem {
	/** Stable React key — unique per (feed entry, attachment) pair. */
	key: string;
	mediaType: NoteMediaType;
	/** Photo/video/audio: the absolute upload URL. File: the download link. Link: the href. */
	url: string;
	/** Position of the owning entry in the notes feed, so an item can point back at its note. */
	noteIndex: number;
	/** The owning note (audio clips render their player and transcription straight from it). */
	note: any;
	/** File rows: the attachment's file name. Link rows: the link's name. Unused by tiles. */
	title: string;
	/** Link rows only: the host shown next to the name. */
	subtitle: string;
	/**
	 * File rows only: the attachment is hidden from clients, so its row carries the same
	 * "Internal Only" marker the notes timeline puts on the log entry that attached it.
	 */
	isInternalOnly: boolean;
	addedAt: string;
	addedBy: string;
	addedByContact: any;
}

export interface NoteMediaBuckets {
	/** Photos and videos interleaved in the order they were shared — the Media grid. */
	media: NoteMediaItem[];
	/** Attachments and voice notes interleaved — the Files list. */
	files: NoteMediaItem[];
	links: NoteMediaItem[];
	/** Files and links merged chronologically — the single list the All view shows. */
	filesAndLinks: NoteMediaItem[];
	total: number;
}

// A note carries at most one attachment per field (the composer sends one note per photo), so the
// media list for a conversation is simply every non-empty attachment field across its notes.
const NOTE_MEDIA_FIELDS: { field: string; mediaType: NoteMediaType }[] = [
	{ field: 'photo', mediaType: NOTE_MEDIA_TYPE_PHOTO },
	{ field: 'video', mediaType: NOTE_MEDIA_TYPE_VIDEO },
	{ field: 'audio', mediaType: NOTE_MEDIA_TYPE_AUDIO },
];

// Documents are never attached to a note — they are attached to the work order, and the notes
// response reports each change as a log entry alongside the notes themselves (the same entries
// the feed renders as "Attached: <file>"). Each pair is a before/after snapshot of one attachment
// collection, so an upload shows up as a file present in `to` but not in `from`.
// The last two collections are internal: `MessageLog` labels their log entries "Internal
// Attachments" and badges them as hidden from clients, so a file taken from them carries that badge
// into the Files list rather than sitting there indistinguishable from a shared attachment.
const ATTACHMENT_LOG_FIELDS: { fromField: string; toField: string; isInternalOnly: boolean }[] = [
	{ fromField: 'fromBuyerAttachments', toField: 'toBuyerAttachments', isInternalOnly: false },
	{
		fromField: 'fromSupplierAttachments',
		toField: 'toSupplierAttachments',
		isInternalOnly: false,
	},
	{
		fromField: 'fromSupplierInternalAttachments',
		toField: 'toSupplierInternalAttachments',
		isInternalOnly: true,
	},
	{ fromField: 'fromAttachments', toField: 'toAttachments', isInternalOnly: true },
];

// A voice note has no file name of its own, so it is labelled the way the design labels it.
const AUDIO_CLIP_TITLE = 'Audio Clip';

/**
 * "Aug 04, 2026 8:00am" — how every row and preview in the media view stamps an item. The full date
 * reads unambiguously in a list that can span months, unlike the relative "Today / Yesterday"
 * wording the chat feed uses on its bubbles.
 */
export const NOTE_MEDIA_DATE_FORMAT = 'MMM DD, YYYY h:mma';

// Long note text still has to fit on one line next to the author and date, so a link that borrows
// its name from the surrounding sentence is cut off well before it can crowd them out.
const LINK_TITLE_MAX_LENGTH = 100;

// Optimistic notes carry a moment object rather than a string, and both are safe to hand to moment().
const getTimestamp = (item: NoteMediaItem): number => {
	// Checked before moment sees it: `moment(undefined)` is NOW, not an invalid date, so an entry
	// carrying neither timestamp would sort as the newest thing in the feed rather than the oldest.
	if (!item.addedAt) {
		return 0;
	}
	const timestamp = moment(item.addedAt).valueOf();
	return isNaN(timestamp) ? 0 : timestamp;
};

// The feed itself reads oldest → newest, and so does every list in this view.
const byOldestFirst = (left: NoteMediaItem, right: NoteMediaItem): number =>
	getTimestamp(left) - getTimestamp(right);

const truncate = (text: string, maxLength: number): string =>
	text.length > maxLength ? `${text.slice(0, maxLength).trim()}…` : text;

// The feed carries timeline entries alongside the notes people actually wrote. Only the latter can
// hold a photo, a voice note or a link — the former are read for their attachment changes instead.
const NON_NOTE_ENTRY_TYPES = [
	NOTE_TYPE_LOG,
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
];

const isNoteEntry = (entry: any): boolean => NON_NOTE_ENTRY_TYPES.indexOf(entry.type) === -1;

// Whoever added a note is recorded on `noteAddedBy*`; whoever changed a work order (and so
// attached a file) is recorded on `changedBy*`. Both flow through the same feed.
const getEntryAuthor = (entry: any) => ({
	addedAt: entry.noteAddedAt || entry.changedAt,
	addedBy: entry.noteAddedBy || entry.changedBy,
	addedByContact: nullSafeGetOrElse(
		'noteAddedByContact',
		entry,
		nullSafeGet('changedByContact', entry)
	),
});

// The plain text of a note. Notes with @mentions keep the flattened sentence on `text`, so that is
// the reliable source; notes that only carry elements fall back to their text runs.
const getNoteText = (note: any): string => {
	if (note.text) {
		return note.text;
	}
	if (Array.isArray(note.elements)) {
		return note.elements
			.filter((element) => element && element.type !== 'user')
			.map((element) => nullSafeGetOrElse('value', element, ''))
			.join('');
	}
	return '';
};

const collectNoteMedia = (note: any, noteIndex: number, items: NoteMediaItem[]) => {
	const author = getEntryAuthor(note);
	NOTE_MEDIA_FIELDS.forEach(({ field, mediaType }) => {
		const url = note[field];
		if (!url || typeof url !== 'string') {
			return;
		}
		items.push({
			key: `${mediaType}-${nullSafeGetOrElse('id', note, noteIndex)}-${url}`,
			mediaType,
			url,
			noteIndex,
			note,
			title: mediaType === NOTE_MEDIA_TYPE_AUDIO ? AUDIO_CLIP_TITLE : '',
			subtitle: '',
			isInternalOnly: false,
			...author,
		});
	});
};

// A note that was nothing but a link leaves whatever punctuation surrounded it as the only plain
// text — a full stop, "( )", the autolink form's "< >", a Markdown wrapper's "`` ``" or "** **", a
// heading's "#". None of that is a name. Asking what is LEFT after stripping punctuation meant
// enumerating the punctuation, and every wrapper anyone thought of was another character to add, so
// the test asks the other way round: a title has to contain a letter or a number. The unicode
// property escapes keep that true of any script, not just Latin (babel compiles them down to
// explicit ranges for Hermes).
const LETTER_OR_NUMBER_PATTERN = /[\p{L}\p{N}]/u;

const hasSubstantiveText = (text: string): boolean => LETTER_OR_NUMBER_PATTERN.test(text);

const collectNoteLinks = (note: any, noteIndex: number, items: NoteMediaItem[]) => {
	const segments = splitTextByLinks(getNoteText(note));
	const linkSegments = segments.filter((segment) => !!segment.href);
	if (linkSegments.length === 0) {
		return;
	}

	// A note that shared exactly one link lends it the rest of its sentence as a name — that is the
	// closest thing to a title we have without an unfurl service. A note carrying several links has
	// no sentence to hand any one of them, so each falls back to a name built from its own URL.
	const surroundingText = segments
		.filter((segment) => !segment.href)
		.map((segment) => segment.value)
		.join(' ')
		.replace(/\s+/g, ' ')
		.trim();
	const canUseSurroundingText = linkSegments.length === 1 && hasSubstantiveText(surroundingText);

	const author = getEntryAuthor(note);
	linkSegments.forEach((segment, linkIndex) => {
		const hostname = getLinkHostname(segment.value);
		const title = canUseSurroundingText
			? truncate(surroundingText, LINK_TITLE_MAX_LENGTH)
			: getLinkFallbackTitle(segment.value);
		items.push({
			key: `link-${nullSafeGetOrElse('id', note, noteIndex)}-${linkIndex}-${segment.value}`,
			mediaType: NOTE_MEDIA_TYPE_LINK,
			url: segment.href,
			noteIndex,
			note,
			title,
			// A bare host has nothing left to say underneath its own name, so it is left off rather
			// than printed twice.
			subtitle: title === hostname ? '' : hostname,
			isInternalOnly: false,
			...author,
		});
	});
};

/**
 * Replay every attachment change in the feed to arrive at the attachments that are still on the
 * work order. Each log entry is a before/after pair, so a file added by one entry and taken away by
 * a later one never reaches the list — the same liveness rule `MessageLog` applies when it greys
 * out a "(Removed)" attachment link.
 */
const getAttachmentItems = (entries: any[]): NoteMediaItem[] => {
	// Keyed by fileId, insertion-ordered, so surviving attachments keep the order they arrived in.
	const liveAttachments = new Map<string, NoteMediaItem>();

	entries.forEach((entry, entryIndex) => {
		const author = getEntryAuthor(entry);
		ATTACHMENT_LOG_FIELDS.forEach(({ fromField, toField, isInternalOnly }) => {
			const previousAttachments = Array.isArray(entry[fromField]) ? entry[fromField] : [];
			const nextAttachments = Array.isArray(entry[toField]) ? entry[toField] : [];
			if (previousAttachments.length === 0 && nextAttachments.length === 0) {
				return;
			}

			const previousFileIds = new Set(previousAttachments.map((attachment) => attachment.fileId));
			const nextFileIds = new Set(nextAttachments.map((attachment) => attachment.fileId));

			nextAttachments.forEach((attachment) => {
				if (!attachment.fileId || previousFileIds.has(attachment.fileId)) {
					return;
				}
				liveAttachments.set(attachment.fileId, {
					key: `file-${attachment.fileId}`,
					mediaType: NOTE_MEDIA_TYPE_FILE,
					url: getFileLink(attachment.fileId, attachment.fileName),
					noteIndex: entryIndex,
					note: entry,
					title: nullSafeGetOrElse('fileName', attachment, ''),
					subtitle: '',
					isInternalOnly,
					...author,
				});
			});

			previousAttachments.forEach((attachment) => {
				if (attachment.fileId && !nextFileIds.has(attachment.fileId)) {
					liveAttachments.delete(attachment.fileId);
				}
			});
		});
	});

	return Array.from(liveAttachments.values());
};

/**
 * Everything shared in a notes feed, split the way the Media, Files & Links view presents it.
 * Photos, videos, voice notes and links come from the notes; documents come from the attachment
 * change logs that ride alongside them in the same response. Nothing extra is fetched.
 */
export const getNoteMediaBuckets = (notes: any[]): NoteMediaBuckets => {
	const noteItems: NoteMediaItem[] = [];
	const entries = (notes || []).filter(Boolean);

	entries.forEach((entry, entryIndex) => {
		if (!isNoteEntry(entry)) {
			return;
		}
		collectNoteMedia(entry, entryIndex, noteItems);
		collectNoteLinks(entry, entryIndex, noteItems);
	});

	const attachmentItems = getAttachmentItems(entries);

	const media = noteItems
		.filter(
			(item) => item.mediaType === NOTE_MEDIA_TYPE_PHOTO || item.mediaType === NOTE_MEDIA_TYPE_VIDEO
		)
		.sort(byOldestFirst);
	const files = noteItems
		.filter((item) => item.mediaType === NOTE_MEDIA_TYPE_AUDIO)
		.concat(attachmentItems)
		.sort(byOldestFirst);
	const links = noteItems
		.filter((item) => item.mediaType === NOTE_MEDIA_TYPE_LINK)
		.sort(byOldestFirst);

	return {
		media,
		files,
		links,
		filesAndLinks: files.concat(links).sort(byOldestFirst),
		total: media.length + files.length + links.length,
	};
};

/** Display name for whoever shared an item, falling back to their email. */
export const getNoteMediaAuthorName = (item: NoteMediaItem): string => {
	const nameGiven = nullSafeGetOrElse('addedByContact.nameGiven', item, '');
	const nameFamily = nullSafeGetOrElse('addedByContact.nameFamily', item, '');
	const fullName = `${nameGiven} ${nameFamily}`.trim();
	return fullName || nullSafeGetOrElse('addedBy', item, '');
};

/**
 * Clip length as the design writes it — zero-padded minutes ("00:10"), unlike the player's own
 * `formatAudioTime`, which drops the leading zero because it counts up while a clip plays.
 */
export const formatClipDuration = (seconds: number): string => {
	if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
		return '';
	}
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);
	const padded = (value: number) => (value < 10 ? `0${value}` : `${value}`);
	return `${padded(minutes)}:${padded(remainingSeconds)}`;
};
